import React from 'react';
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import cx from './cx';
import { Button } from './Button';

/* ------------------------------------------------------------ EmptyState */

export function EmptyState({ icon: Icon, title, description, action, secondaryAction, className, compact = false }) {
  return (
    <div className={cx('flex flex-col items-center justify-center text-center', compact ? 'gap-1.5 px-4 py-6' : 'gap-2 px-6 py-10', className)}>
      {Icon && (
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-surface-2 text-fg-muted">
          <Icon className="h-4 w-4" aria-hidden="true" />
        </span>
      )}
      <div className="max-w-sm">
        <p className="text-sm font-semibold text-fg">{title}</p>
        {description && <p className="mt-1 text-sm text-fg-muted">{description}</p>}
      </div>
      {(action || secondaryAction) && <div className="mt-1 flex flex-wrap justify-center gap-2">{action}{secondaryAction}</div>}
    </div>
  );
}

/** Error state with retry, for failed data loads. */
export function ErrorState({ title = 'Something went wrong', description = 'We could not load this. Check your connection and try again.', onRetry, className }) {
  return (
    <div role="alert" className={cx('flex flex-col items-center gap-3 px-6 py-12 text-center', className)}>
      <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-loss-soft text-loss">
        <XCircle className="h-5 w-5" aria-hidden="true" />
      </span>
      <div className="max-w-sm">
        <p className="text-sm font-semibold text-fg">{title}</p>
        <p className="mt-1 text-sm text-fg-muted">{description}</p>
      </div>
      {onRetry && <Button size="sm" onClick={onRetry}>Try again</Button>}
    </div>
  );
}

/* ----------------------------------------------------------------- Badge */

const BADGE_TONES = {
  neutral: 'bg-surface-2 text-fg-muted border-line',
  accent: 'bg-accent-soft text-accent border-transparent',
  success: 'bg-success-soft text-success border-transparent',
  loss: 'bg-loss-soft text-loss border-transparent',
  be: 'bg-be-soft text-be border-transparent',
  warn: 'bg-warn-soft text-warn border-transparent',
};

export function Badge({ tone = 'neutral', icon: Icon, className, children }) {
  return (
    <span className={cx('inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium', BADGE_TONES[tone] || BADGE_TONES.neutral, className)}>
      {Icon && <Icon className="h-3 w-3" aria-hidden="true" />}
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------- P&L */

/** `+$1,234.50` / `-$80.00` / `$0.00`. */
export function formatPnl(value, { decimals = 2, signed = true, compact = false } = {}) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';
  const abs = Math.abs(numeric);
  let body;
  if (compact && abs >= 1000) {
    body = `${(abs / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })}k`;
  } else {
    body = abs.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  }
  const sign = numeric < 0 ? '-' : signed && numeric > 0 ? '+' : '';
  return `${sign}$${body}`;
}

export function pnlToneClass(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric === 0) return 'text-fg-muted';
  return numeric > 0 ? 'text-success' : 'text-loss';
}

export function PnlText({ value, className, as: Component = 'span', ...options }) {
  return <Component className={cx('tabular-nums', pnlToneClass(value), className)}>{formatPnl(value, options)}</Component>;
}

/* -------------------------------------------------------------- StatTile */

export function StatTile({ label, value, sublabel, tone, delta, className, children }) {
  const toneClass = tone === 'success' ? 'text-success' : tone === 'loss' ? 'text-loss' : tone === 'accent' ? 'text-accent' : 'text-fg';
  return (
    <div className={cx('min-w-0 rounded-panel border border-line bg-surface p-3.5 shadow-card', className)}>
      <div className="flex items-center justify-between gap-2">
        <p className="truncate text-xs font-medium text-fg-muted">{label}</p>
      </div>
      <p className={cx('mt-1.5 text-lg font-semibold leading-tight tabular-nums [overflow-wrap:anywhere] sm:text-xl 2xl:text-2xl', toneClass)}>{value}</p>
      {(sublabel || delta) && (
        <p className="mt-0.5 flex items-center gap-1.5 truncate text-xs text-fg-muted">
          {delta}
          {sublabel}
        </p>
      )}
      {children}
    </div>
  );
}

/* --------------------------------------------------------------- Callout */

const CALLOUT = {
  info: { icon: Info, cls: 'border-accent/30 bg-accent-soft', iconCls: 'text-accent' },
  success: { icon: CheckCircle2, cls: 'border-success/30 bg-success-soft', iconCls: 'text-success' },
  warning: { icon: AlertTriangle, cls: 'border-warn/30 bg-warn-soft', iconCls: 'text-warn' },
  danger: { icon: XCircle, cls: 'border-loss/30 bg-loss-soft', iconCls: 'text-loss' },
};

export function Callout({ tone = 'info', title, children, action, className }) {
  const config = CALLOUT[tone] || CALLOUT.info;
  const Icon = config.icon;
  return (
    <div role={tone === 'danger' ? 'alert' : undefined} className={cx('flex items-start gap-3 rounded-panel border px-4 py-3 text-sm', config.cls, className)}>
      <Icon className={cx('mt-0.5 h-4 w-4 shrink-0', config.iconCls)} aria-hidden="true" />
      <div className="min-w-0 flex-1 text-fg">
        {title && <p className="font-medium">{title}</p>}
        {children && <div className={title ? 'mt-0.5 text-fg-muted' : undefined}>{children}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}
